import React, { useEffect, useMemo, useState } from "react";
import {
  enforceTierComposition,
  servingApi,
  type AssessmentItem,
  type Lang,
  type SubmitInput,
  type SubmitResult,
  type Tier,
} from "../../../api/serving";
import { chassis } from "./shared/chassis";
import { peT, type PeLessonStrings } from "../../../i18n/peLesson";
import LanguageToggle from "./shared/LanguageToggle";
import { useChapterNumber } from "./shared/useChapterNumber";
import FormativeItem, { type ItemAnswer } from "./items/FormativeItem";

export interface FormativeAssessmentProps {
  chapterId: string;
  /** The student's tier — fixed for the assignment. */
  tier: Tier;
  /** Per-assignment language setting from the LTI launch. */
  lang: Lang;
  /** Token-derived launch role. Teachers get a read-only preview (no submit). */
  role: "student" | "teacher";
  /** From the LTI launch URL; required by the live /pe/submit rail. */
  studentAssignmentId: string | null;
  /** "quiz" → summative chapter quiz via /pe/quiz (no practice gate). Defaults to formative. */
  source?: "assessment" | "quiz";
  /**
   * Embedded mode: drop the full-page chrome (min-h-screen / background / own
   * header + LanguageToggle) so the host (e.g. the LessonSequence summative
   * step) owns the page layout + "Mark complete & continue". The item runner +
   * per-item /pe/submit are unchanged.
   */
  embedded?: boolean;
  /**
   * Fired once every item has been submitted (the component's `allDone` state).
   * The native host (LessonSequence formative step) uses this to gate its
   * "Mark complete & continue" — the step can only complete after all items are
   * answered, mirroring the LTI surface (which has no early host button). On LTI
   * this is unused (the component renders its own "All done!" summary).
   */
  onAllDone?: () => void;
}

function answerReady(item: AssessmentItem, a: ItemAnswer | undefined): boolean {
  if (!a) return false;
  // Live audio_match items are oral (response_mode="audio"). WS2 Task 3: a real
  // recording (blob) gates Submit (the "I said it" stub is gone).
  if (item.type === "audio_match" && item.response_mode === "audio") return !!a.audio;
  if (item.type === "mc" || item.type === "audio_match") return !!a.choiceId;
  if (item.type === "audio") return !!a.audio;
  return (a.text ?? "").trim().length > 0; // sentence_builder, open_response
}

// Amendment (locked pedagogy rule): before the first SCORED item of each novel
// interaction type in a session, show one non-scored, Star-style practice item
// of the same interaction. Audio-capture (oral audio_match + audio) share a key.
function interactionKey(it: AssessmentItem): string {
  if ((it.type === "audio_match" && it.response_mode === "audio") || it.type === "audio")
    return "audio";
  return it.type;
}

// Fixed practice content per interaction type (intentionally trivial; it teaches
// the gesture, not the concept). Rendered with the real item renderer so the
// practice interaction matches the scored one exactly.
function practiceItemFor(key: string, tier: Tier, language: Lang): AssessmentItem {
  const base = {
    source_id: `practice-${key}`,
    item_id: `practice-${key}`,
    chapter_id: "practice",
    scene_id: "practice",
    tier,
    language,
  };
  // Run E2 J5: the practice text lives in the EN/ES lesson string table.
  const p = peT(language).practice;
  switch (key) {
    case "mc":
      return {
        ...base,
        type: "mc",
        response_mode: "auto",
        prompt: p.mcPrompt,
        choices: [
          { id: "a", text: p.mcChoiceA, audio_url: null },
          { id: "b", text: p.mcChoiceB, audio_url: null },
        ],
      };
    case "audio":
      return {
        ...base,
        type: "audio_match",
        response_mode: "audio",
        prompt: p.audioPrompt,
      };
    case "sentence_builder":
      return {
        ...base,
        type: "sentence_builder",
        response_mode: "auto",
        prompt: p.sentencePrompt,
        word_bank: p.sentenceWords,
      };
    case "drag_drop":
      return {
        ...base,
        type: "drag_drop",
        response_mode: "auto",
        prompt: p.dragPrompt,
        drag_items: p.dragItems,
        drag_targets: p.dragTargets,
      };
    case "open_response":
    default:
      return {
        ...base,
        type: "open_response",
        response_mode: "auto",
        prompt: p.openPrompt,
      };
  }
}

const PracticeGate: React.FC<{ practiceItem: AssessmentItem; onReady: () => void; t: PeLessonStrings }> = ({
  practiceItem,
  onReady,
  t,
}) => {
  const [a, setA] = useState<ItemAnswer>({});
  return (
    <div className="bg-white rounded-lg border p-6" style={{ borderColor: chassis.accent }}>
      <div className="flex items-center gap-2 mb-1">
        <span style={{ fontSize: 26 }}>⭐</span>
        <h2 className="text-lg font-bold" style={{ color: chassis.textPrimary }}>
          {t.practiceTitle}
        </h2>
      </div>
      <p className="text-sm mb-4" style={{ color: chassis.textSecondary }}>
        {t.practiceNote}
      </p>
      <p className="text-lg font-medium mb-4" style={{ color: chassis.textPrimary }}>
        {practiceItem.prompt}
      </p>
      <FormativeItem item={practiceItem} value={a} onChange={setA} disabled={false} />
      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={onReady}
          className="px-6 py-2.5 rounded-md font-medium"
          style={{ backgroundColor: chassis.accent, color: chassis.accentText }}
        >
          {t.imReady}
        </button>
      </div>
    </div>
  );
};

const FormativeAssessment: React.FC<FormativeAssessmentProps> = ({ chapterId, tier, lang, role, studentAssignmentId, source = "assessment", embedded = false, onAllDone }) => {
  const isTeacher = role === "teacher";
  const isQuiz = source === "quiz";
  const chapterNumber = useChapterNumber(chapterId);
  const [displayLang, setDisplayLang] = useState<Lang>(lang);
  // Run E2 J5 (D04): every player string follows the language on screen.
  const t = peT(displayLang);
  const [items, setItems] = useState<AssessmentItem[]>([]);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, ItemAnswer>>({});
  const [results, setResults] = useState<Record<string, SubmitResult>>({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Interaction types already practiced this session (amendment).
  const [practicedTypes, setPracticedTypes] = useState<Set<string>>(new Set());
  // Real DB practice items (practice-*-en/-es), keyed by interaction type, so the
  // practice gate plays actual prompt audio instead of the synthetic fallback.
  const [dbPractice, setDbPractice] = useState<Record<string, AssessmentItem>>({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = isQuiz
          ? await servingApi.getQuiz({ chapterId, tier, language: displayLang })
          : await servingApi.getAssessments({ chapterId, tier, language: displayLang });
        // Real DB practice items (chapter_id IS NULL; tier T1 gesture set). These
        // carry prompt audio; the practice gate uses them instead of the synthetic
        // client-side items so every T1 practice plays its narration. Non-fatal:
        // if it fails we fall back to the synthetic items below. Quizzes are
        // summative — they never show the practice gate, so skip the fetch.
        let practiceMap: Record<string, AssessmentItem> = {};
        if (!isQuiz) {
          try {
            const pr = await servingApi.getAssessments({
              chapterId: "practice",
              tier: "T1",
              language: displayLang,
            });
            for (const p of pr.items) practiceMap[interactionKey(p)] = p;
          } catch {
            practiceMap = {};
          }
        }
        if (!cancelled) {
          // Defensive: enforce the locked tier rule client-side too
          // (open_response must never render at T1/T2).
          setItems(enforceTierComposition(tier, res.items));
          setDbPractice(practiceMap);
          setIndex(0);
          setAnswers({});
          setResults({});
          setPracticedTypes(new Set());
          setError(null);
        }
      } catch (e) {
        // Raw API text stays in the console; the student sees a short message.
        console.warn("[FormativeAssessment] items load failed:", e);
        if (!cancelled) setError("load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [chapterId, tier, displayLang]);

  const item = items[index];
  const result = item ? results[item.item_id] : undefined;
  const answer = item ? answers[item.item_id] : undefined;

  const summary = useMemo(() => {
    const submitted = Object.values(results);
    const correct = submitted.filter((r) => r.is_correct === true).length;
    const queuedAudio = submitted.filter((r) => r.status === "queued_for_review" && r.response_mode === "audio").length;
    const queuedTyped = submitted.filter((r) => r.status === "queued_for_review" && r.response_mode !== "audio").length;
    const queued = queuedAudio + queuedTyped;
    return { submitted: submitted.length, correct, queued, queuedAudio, queuedTyped };
  }, [results]);

  // Signal the host once every item has been submitted (D4 gate). The native
  // formative step keeps its "Mark complete & continue" hidden until this fires,
  // so the step can't complete before all items (incl. the oral item) are done.
  useEffect(() => {
    if (items.length > 0 && summary.submitted === items.length) onAllDone?.();
  }, [items.length, summary.submitted, onAllDone]);

  const setAnswer = (a: ItemAnswer) => {
    if (!item) return;
    setAnswers((prev) => ({ ...prev, [item.item_id]: a }));
  };

  const handleSubmit = async () => {
    if (!item || !answer) return;
    setSubmitting(true);
    try {
      // For mc: resolve choice text from the item's choices array so the backend
      // can score it via normalized string equality against answer_key_json.answer.
      const choiceText =
        item.type === "mc" && answer.choiceId
          ? (item.choices ?? []).find((c) => c.id === answer.choiceId)?.text
          : undefined;
      const input: SubmitInput = {
        item_id: item.item_id,
        source_id: item.source_id,
        chapter_id: item.chapter_id,
        tier: item.tier,
        language: item.language,
        type: item.type,
        response_mode: item.response_mode,
        student_assignment_id: studentAssignmentId,
        choice_id: answer.choiceId,
        text: choiceText ?? answer.text,
        audio: answer.audio ?? null,
      };
      const res = await servingApi.submitItem(input);
      setResults((prev) => ({ ...prev, [item.item_id]: res }));
      // Auto-advance to next item after a brief moment so the result is visible,
      // unless this is the last item (stay on the summary screen).
      setTimeout(() => {
        setIndex((i) => Math.min(i + 1, items.length - 1));
      }, 1200);
    } catch (e) {
      console.warn("[FormativeAssessment] submit failed:", e);
      setError("submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        className={embedded ? "flex items-center justify-center py-12" : "min-h-screen flex items-center justify-center"}
        style={embedded ? undefined : { background: chassis.background }}
      >
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: chassis.accent }} />
      </div>
    );
  }
  if (error) {
    return (
      <div
        className={embedded ? "flex items-center justify-center py-12" : "min-h-screen flex items-center justify-center"}
        style={embedded ? undefined : { background: chassis.background }}
      >
        <p className="text-red-600 font-medium px-6 text-center">
          {error === "submit" ? t.submitFailed : t.loadItemsFailed}
        </p>
      </div>
    );
  }

  const allDone = items.length > 0 && summary.submitted === items.length;
  // Show the practice gate before the first (unanswered) item of a novel type.
  // Summative quizzes never show the practice gate.
  const needsPractice = !isQuiz && !!item && !result && !practicedTypes.has(interactionKey(item));

  return (
    <div className={embedded ? "" : "min-h-screen"} style={embedded ? undefined : { background: chassis.background }}>
      <div className={embedded ? "" : "max-w-2xl mx-auto px-4 py-8"}>
        {/* Full-page header (chapter/tier title + language toggle). Dropped in
            embedded mode — the host step provides the title + the lang is the
            assignment's fixed setting. */}
        {!embedded && (
          <div className="flex items-start justify-between gap-4 mb-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: chassis.icon }}>
                {t.chapter} {chapterNumber ?? "…"} · {t.tier(tier)} · {t.langName[displayLang]}
              </p>
              <h1 className="text-2xl font-bold" style={{ color: chassis.textPrimary }}>
                {isQuiz ? t.quizTitle : t.formativeTitle}
              </h1>
            </div>
            <LanguageToggle value={displayLang} onChange={setDisplayLang} assignmentDefault={lang} t={t} />
          </div>
        )}

        {/* progress */}
        <div className="h-2 rounded-full bg-gray-200 overflow-hidden mb-6 mt-3">
          <div
            className="h-full transition-all"
            style={{
              width: `${(summary.submitted / Math.max(items.length, 1)) * 100}%`,
              backgroundColor: chassis.accent,
            }}
          />
        </div>

        {isTeacher && (
          <div
            className="mb-4 rounded-md px-4 py-3 text-sm flex items-center gap-2"
            style={{ backgroundColor: "rgb(254 249 195)", color: chassis.textPrimary }}
          >
            <span aria-hidden>👁</span>
            <span><strong>{t.teacherPreviewStrong}</strong> {t.teacherPreviewBody}</span>
          </div>
        )}

        {allDone ? (
          <div className="bg-white rounded-lg border p-6 text-center" style={{ borderColor: chassis.accent }}>
            <h2 className="text-xl font-bold mb-3" style={{ color: chassis.textPrimary }}>
              {t.allDone}
            </h2>
            <p style={{ color: chassis.textSecondary }}>
              {t.scoreLine(summary.correct, summary.submitted - summary.queued)}
            </p>
            {summary.queuedAudio > 0 && (
              <p className="mt-2" style={{ color: chassis.textSecondary }}>
                {t.spokenQueued(summary.queuedAudio)}
              </p>
            )}
            {summary.queuedTyped > 0 && (
              <p className="mt-2" style={{ color: chassis.textSecondary }}>
                {t.writtenQueued(summary.queuedTyped)}
              </p>
            )}
          </div>
        ) : needsPractice && item ? (
          <PracticeGate
            practiceItem={
              dbPractice[interactionKey(item)] ??
              practiceItemFor(interactionKey(item), item.tier, item.language)
            }
            onReady={() =>
              setPracticedTypes((prev) => new Set(prev).add(interactionKey(item)))
            }
            t={t}
          />
        ) : (
          item && (
            <div className="bg-white rounded-lg border p-6" style={{ borderColor: "rgb(229 231 235)" }}>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm" style={{ color: chassis.textSecondary }}>
                  {t.itemOf(index + 1, items.length)}
                </span>
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{ backgroundColor: "rgb(243 244 246)", color: chassis.textSecondary }}
                >
                  {t.itemKind[
                    interactionKey(item) === "audio"
                      ? "audio"
                      : item.type === "audio_match"
                      ? "mc"
                      : (item.type as keyof PeLessonStrings["itemKind"])
                  ] ?? ""}
                </span>
              </div>

              <p className="text-lg font-medium mb-4" style={{ color: chassis.textPrimary }}>
                {item.prompt}
              </p>

              <FormativeItem key={item.item_id} item={item} value={answer ?? {}} onChange={setAnswer} disabled={!!result} />

              {/* result */}
              {result && (
                <div
                  className="mt-4 rounded-md p-3 text-sm"
                  style={{
                    backgroundColor:
                      result.status === "queued_for_review"
                        ? "rgb(254 249 195)"
                        : result.is_correct
                        ? "rgb(220 252 231)"
                        : "rgb(254 226 226)",
                    color: chassis.textPrimary,
                  }}
                >
                  {/* The live /pe/submit sends no feedback text; show a short
                      localized result line (server text wins if it ever comes). */}
                  {result.feedback ||
                    (result.status === "queued_for_review"
                      ? t.resultQueued
                      : result.is_correct === true
                      ? t.resultCorrect
                      : result.is_correct === false
                      ? t.resultIncorrect
                      : t.resultSaved)}
                  {result.status === "queued_for_review" && (
                    <span className="block text-xs mt-1 opacity-80">{t.heldForReview}</span>
                  )}
                </div>
              )}

              {/* actions */}
              <div className="mt-6 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setIndex((i) => Math.max(0, i - 1))}
                  disabled={index === 0}
                  className="text-sm font-medium disabled:opacity-40"
                  style={{ color: chassis.accent }}
                >
                  {t.previous}
                </button>

                {isTeacher ? (
                  index < items.length - 1 ? (
                    <button
                      type="button"
                      onClick={() => setIndex((i) => Math.min(i + 1, items.length - 1))}
                      className="px-6 py-2.5 rounded-md font-medium"
                      style={{ backgroundColor: chassis.accent, color: chassis.accentText }}
                    >
                      {t.next}
                    </button>
                  ) : (
                    <span className="text-sm" style={{ color: chassis.textSecondary }}>
                      {t.endOfPreview}
                    </span>
                  )
                ) : !result ? (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting || !answerReady(item, answer)}
                    className="px-6 py-2.5 rounded-md font-medium disabled:opacity-50"
                    style={{ backgroundColor: chassis.accent, color: chassis.accentText }}
                  >
                    {submitting ? t.submitting : t.submit}
                  </button>
                ) : index < items.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => setIndex((i) => Math.min(i + 1, items.length - 1))}
                    className="px-6 py-2.5 rounded-md font-medium"
                    style={{ backgroundColor: chassis.accent, color: chassis.accentText }}
                  >
                    {t.next}
                  </button>
                ) : null}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default FormativeAssessment;
